import { useState } from 'react';
import { Actions, F1, Option } from '@client/new/helpers/types';
import { sorting, Sort } from '@client/new/helpers/sorting';
import { useDebounce } from '@client/hooks/use-debounce';
import { LibraryVideo } from './videos.domain';
import { TableListRow, toCell } from './components/TableList';
import { faPencil, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { CellCTAProps } from '@client/new/components/TableShared/Cell';
import { VideosVMDispatcher } from './videos.viewModel';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';

type VideosSortKeys = keyof Pick<
  LibraryVideo,
  'name' | 'createdAt' | 'fileSize'
>;

const getActions = (
  video: LibraryVideo,
  dispatch: VideosVMDispatcher,
): CellCTAProps[] => {
  return [
    {
      type: 'Button',
      value: {
        icon: faPencil,
        onClick: () => dispatch({ type: 'editVideo', payload: video }),
        tooltip: 'edit video',
        color: 'primary',
      },
    },
    {
      type: 'DownloadLink',
      value: {
        icon: faDownload,
        suggestedFilename: `${video.name}.mp4`,
        color: 'neutral',
        to: video.downloadUrl,
        tooltip: 'download video',
      },
    },
    {
      type: 'Button',
      value: {
        icon: faTrash,

        color: 'neutral',
        onClick: () =>
          dispatch({
            type: 'deleteVideo',
            payload: { videoId: video.id + '' },
          }),
        tooltip: 'delete video',
      },
    },
  ];
};

export const toDurationText = (duration: number): string => {
  const hh = Math.floor(duration / (60 * 60));
  const mm = Math.floor(duration / 60);
  const ss = Math.round(duration % 60);
  return [hh, mm, ss].map((v: number) => (v < 10 ? '0' + v : v + '')).join(':');
};

export const toRow = (
  v: LibraryVideo,
  dispatch: VideosVMDispatcher,
): TableListRow => {
  const d = new Date(v.createdAt);

  return {
    id: v.id + '',
    cells: [
      toCell('header', {
        title: v.name.slice(0, 30) + (v.name.length >= 27 ? '...' : ''),
        imageUrl: v.poster,
        subtitle: toDurationText(v.duration),
        onClick: () => dispatch({ type: 'editVideo', payload: v }),
      }),
      toCell('tag', v.duration <= 60 ? 'clip' : 'video'),
      toCell('text', {
        text: `${d.getMonth() + 1}-${d.getDay()}-${d.getFullYear()}`,
      }),
      toCell('actions', getActions(v, dispatch)),
    ],
  };
};

export type VideosListViewModel = {
  list: LibraryVideo[];
  rows: TableListRow[];
  sortOptions: Array<Option<VideosSortKeys>>;
  dispatch: VideosListVMDispatcher;
  sort: Sort<VideosSortKeys>;
  searchText: string;
};

export type VideosListVMActionsMap = {
  searchText: string;
  sort: Sort<VideosSortKeys>;
};

type VideosListVMActions = Actions<VideosListVMActionsMap>;

export type VideosListVMDispatcher = F1<VideosListVMActions, null>;

type VideosListViewModelState = {
  searchText: string;
  sort: Sort<VideosSortKeys>;
};

const sortOptions: VideosListViewModel['sortOptions'] = [
  {
    value: 'name',
    label: 'Name',
  },
  {
    value: 'createdAt',
    label: 'Upload date',
  },
  {
    value: 'fileSize',
    label: 'Size',
  },
];

export type ToVideoListParams = VideosListViewModelState & {
  list: LibraryVideo[];
};

export const toVideosList = (p: ToVideoListParams): LibraryVideo[] => {
  const searchTerm = p.searchText.toLocaleLowerCase();
  const filtered = p.searchText
    ? p.list.filter((v) => v.name.toLocaleLowerCase().includes(searchTerm))
    : p.list;
  const sorted = p.sort ? sorting(p.sort, filtered) : filtered;
  return sorted;
};

export const useVideosList = (videos: LibraryVideo[]): VideosListViewModel => {
  const [viewModelState, setViewModelState] =
    useState<VideosListViewModelState>({
      searchText: '',
      sort: { type: 'desc', field: 'createdAt' },
    });
  const debouncedSearchText = useDebounce(viewModelState.searchText, 300);
  const list = toVideosList({
    list: videos,
    searchText: debouncedSearchText,
    sort: viewModelState.sort,
  });

  const dispatch: VideosListVMDispatcher = (action) => {
    switch (action.type) {
      case 'searchText':
        setViewModelState({ ...viewModelState, searchText: action.payload });
        return null;
      case 'sort':
        setViewModelState({ ...viewModelState, sort: action.payload });
        return null;
    }
  };

  return {
    ...viewModelState,
    list,
    rows: list.map((v) => toRow(v, console.log as any)),
    dispatch,
    sortOptions,
  };
};
